/**
 * Condition reminders due a message, and the button that drafts them (Lloyd, 14 Sep 2026:
 * *"Yes, please build this."*).
 *
 * The ladder and its wording have existed since 28 August. Nothing ever called them, so no client
 * has ever received one. This is where a person sends them.
 *
 * ## It drafts. It does not send.
 *
 * Lloyd was told the reminders would come back for review before anything went out, so the button
 * writes drafts into the client mailbox and a person sends each one. The same rule the stale-case
 * chasing screen follows, and the screen says so rather than leaving it to be discovered.
 *
 * Looking is always allowed; drafting is behind `condition_reminders.enabled`, which ships off. The
 * list renders either way, because seeing who is due is how somebody decides whether to turn it on.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

interface DueReminderDto {
  reminderId: number;
  ticketId: number;
  reminderType: string;
  offsetDays: number;
  clientEmail: string | null;
  clientFirstName: string | null;
  deadline: string | null;
}

/**
 * A court-ordered payment coming due (Kimberly, 14 Sep 2026).
 *
 * Every field optional, per the rule that cost the settings page once: an older API returns no
 * `payments` key and this screen must still render the reminders it does return.
 */
interface DuePaymentDto {
  conditionId?: number;
  ticketId?: number;
  ticketNumber?: string | null;
  dueAt?: string | null;
  amountCents?: number | null;
  daysUntilDue?: number;
  overdue?: boolean;
  clientFirstName?: string | null;
  clientLastName?: string | null;
  clientEmail?: string | null;
}

interface DueResponse {
  enabled: boolean;
  due: DueReminderDto[];
  payments?: DuePaymentDto[];
}

const TYPE_LABEL: Record<string, string> = {
  condition_reminder_dsc: 'Driving safety course',
  condition_reminder_driving_record: 'Driving record',
  condition_reminder_affidavit: 'Affidavit',
};

/** "$450.00", or a dash when the court ordered an amount nobody recorded. */
function formatAmountCents(amountCents: number | null | undefined): string {
  if (amountCents === null || amountCents === undefined) return '—';
  return `$${(amountCents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** How far off the deadline is, in the words somebody working the list would use. */
function describeDueIn(days: number | undefined): string {
  if (days === undefined) return '—';
  if (days < 0) return `${Math.abs(days)} day(s) overdue`;
  if (days === 0) return 'due today';
  return `in ${days} day(s)`;
}

/** The rung, said the way staff would say it. Negative means the deadline has passed. */
function describeRung(offsetDays: number): string {
  if (offsetDays < 0) return `${Math.abs(offsetDays)} day(s) overdue`;
  if (offsetDays === 0) return 'Due today';
  return `${offsetDays} day(s) before`;
}

const ManagerReminderDraftsView: React.FC = () => {
  const [due, setDue] = useState<DueReminderDto[]>([]);
  const [payments, setPayments] = useState<DuePaymentDto[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [drafting, setDrafting] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const host = process.env.API_HOST || '';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${host}/api/v1/manager/condition-reminders/due`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to load the reminders that are due');
      const body: DueResponse = await response.json();
      setDue(body.due);
      setPayments(body.payments ?? []);
      setEnabled(body.enabled);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the reminders that are due');
    } finally {
      setLoading(false);
    }
  }, [host]);

  useEffect(() => {
    void load();
  }, [load]);

  const draft = async () => {
    setDrafting(true);
    setError(null);
    setSummary(null);
    try {
      const response = await fetch(`${host}/api/v1/manager/condition-reminders/drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.message || body.error || 'Failed to draft the reminders');
      setSummary(body.summary ?? null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to draft the reminders');
    } finally {
      setDrafting(false);
    }
  };

  return (
    <>
      <Container sx={{ mt: 4, mb: 6 }}>
        <Typography variant="h4" gutterBottom>
          Condition reminders due
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Clients who have reached a rung of the reminder ladder. The button writes drafts into the
          client mailbox for you to read and send. Nothing is sent automatically.
        </Typography>

        {!enabled && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Reminders are switched off</AlertTitle>
            The list below is what would be drafted. Turn on condition reminders in Settings to
            enable the button.
          </Alert>
        )}

        {summary && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {summary}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : due.length === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography variant="body1" color="text.secondary">
              Nobody is due a reminder today.
            </Typography>
          </Paper>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Condition</TableCell>
                    <TableCell>Rung</TableCell>
                    <TableCell>Deadline</TableCell>
                    <TableCell>Email</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {due.map((row) => (
                    <TableRow key={row.reminderId} hover>
                      <TableCell>{row.clientFirstName || '—'}</TableCell>
                      <TableCell>{TYPE_LABEL[row.reminderType] ?? row.reminderType}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={row.offsetDays < 0 ? 'warning' : 'default'}
                          label={describeRung(row.offsetDays)}
                        />
                      </TableCell>
                      <TableCell>{row.deadline || '—'}</TableCell>
                      {/* A client with no address cannot be drafted to, and the run reports it
                          rather than stamping the rung as done. Shown here so it is fixable first. */}
                      <TableCell>
                        {row.clientEmail || (
                          <Typography variant="body2" color="warning.main">
                            No email on file
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Button
              variant="contained"
              sx={{ mt: 2 }}
              disabled={!enabled || drafting}
              onClick={() => void draft()}
            >
              {drafting ? <CircularProgress size={20} /> : `Draft ${due.length} reminder(s)`}
            </Button>
          </>
        )}

        {/*
          Payments coming due (Kimberly, 14 Sep 2026: "every client with a payment, DSC or affidavit
          due soon"). The three condition types above are draftable because Lloyd approved a ladder
          for them on 28 August. He has approved NO cadence for chasing money, so these are listed
          and nothing here drafts or sends. Staff decide.
        */}
        <Typography variant="h6" sx={{ mt: 4 }}>
          Payments coming due ({payments.length})
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Court-ordered payments due within 45 days, and anything already overdue. There is no
          automatic reminder for these, so they are listed for someone to act on.
        </Typography>

        {payments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No court-ordered payments are due.
          </Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Case</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell>Email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((row) => (
                  <TableRow key={row.conditionId} hover>
                    <TableCell>
                      {[row.clientFirstName, row.clientLastName]
                        .filter((part) => (part ?? '').trim() !== '')
                        .join(' ') || '—'}
                    </TableCell>
                    <TableCell>{row.ticketNumber || '—'}</TableCell>
                    <TableCell>{formatAmountCents(row.amountCents)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={row.overdue ? 'warning' : 'default'}
                        label={describeDueIn(row.daysUntilDue)}
                      />
                    </TableCell>
                    <TableCell>
                      {row.clientEmail || (
                        <Typography variant="body2" color="warning.main">
                          No email on file
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </>
  );
};

export default ManagerReminderDraftsView;
